import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Button,
  Select,
  FileInput,
  Paper,
  Group,
  Alert,
  Badge,
  Table,
  Checkbox,
  Radio,
  Loader,
  Tooltip
} from '@mantine/core';
import {
  IconUpload,
  IconFileSpreadsheet,
  IconAlertCircle,
  IconCheck,
  IconEye,
  IconInfoCircle
} from '@tabler/icons-react';

import { useAwsCredentials } from '../../../shared/contexts/CredentialsContext';
import { listConnectInstances } from '../aws/connectClient';
import ExcelImporter from '../import/excelImporter';
import ImportValidationService from '../import/importValidationService';
import { listExistingResources, buildNameToIdMaps } from '../import/connectCreateClient';
import { importResources } from '../import/resourceImportDispatcher';
import { buildUserHierarchyStructure } from '../import/reverseDataMappers';
import ImportLogService from '../import/importLogService';
import {
  IMPORT_STATUS,
  CONFLICT_RESOLUTION,
  RESOURCE_TYPE_LABELS,
  IMPORTABLE_RESOURCE_TYPES,
  ERROR_MESSAGES
} from '../import/importConstants';

import Layout from './Layout';
import ImportPreviewModal from './ImportPreviewModal';
import ImportProgressPanel from './ImportProgressPanel';
import ImportLogViewer from './ImportLogViewer';
import AWSCredentialChangeSection from './AWSCredentialChangeSection';

const AWSImportPage = ({ onBackToPlatformSelector }) => {
  const { awsCredentials, setAwsCredentials } = useAwsCredentials();

  // Instance selection
  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [loadingInstances, setLoadingInstances] = useState(false);

  // File upload
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);

  // Validation
  const [validationResults, setValidationResults] = useState(null);
  const [validationSummary, setValidationSummary] = useState(null);
  const [existingResources, setExistingResources] = useState(null);
  const [nameToIdMaps, setNameToIdMaps] = useState(null);

  // Import configuration
  const [selectedResourceTypes, setSelectedResourceTypes] = useState({});
  const [conflictResolution, setConflictResolution] = useState(CONFLICT_RESOLUTION.SKIP);
  const [perResourceConflicts, setPerResourceConflicts] = useState({});

  // Import execution
  const [importProgress, setImportProgress] = useState({});
  const [importLog, setImportLog] = useState(null);
  const [importResults, setImportResults] = useState(null);

  // UI state
  const [importStatus, setImportStatus] = useState(IMPORT_STATUS.IDLE);
  const [errorMessage, setErrorMessage] = useState(null);
  const [previewModalState, setPreviewModalState] = useState({ opened: false, resourceType: null });

  /**
   * Load Connect instances
   */
  const loadConnectInstances = useCallback(async () => {
    if (!awsCredentials) return;

    setLoadingInstances(true);
    setErrorMessage(null);

    try {
      const instancesList = await listConnectInstances(awsCredentials, awsCredentials.region);
      setInstances(instancesList || []);
    } catch (error) {
      console.error('Error loading instances:', error);
      setErrorMessage('Failed to load Connect instances. Please check your credentials.');
    } finally {
      setLoadingInstances(false);
    }
  }, [awsCredentials]);

  // Load instances on mount
  useEffect(() => {
    loadConnectInstances();
  }, [loadConnectInstances]);

  /**
   * Handle instance selection
   */
  const handleInstanceSelect = (instanceId) => {
    const instance = instances.find(i => i.Id === instanceId);
    setSelectedInstance(instance);

    // Reset state when changing instance
    setValidationResults(null);
    setValidationSummary(null);
    setExistingResources(null);
    setImportStatus(IMPORT_STATUS.IDLE);
    setErrorMessage(null);
  };

  /**
   * Handle file upload
   */
  const handleFileUpload = async (file) => {
    if (!file) {
      return;
    }

    setUploadedFile(file);
    setImportStatus(IMPORT_STATUS.VALIDATING);
    setErrorMessage(null);

    try {
      // Parse Excel file
      const importer = new ExcelImporter();
      await importer.loadFile(file);

      // Validate sheets exist
      const sheetValidation = importer.validateSheets();
      if (!sheetValidation.valid) {
        throw new Error(
          `Missing required sheets: ${sheetValidation.missingSheets.join(', ')}`
        );
      }

      // Parse all resources
      const parsed = importer.parseAll();

      // Transform user hierarchies to have proper name field for display
      if (parsed.userHierarchies && parsed.userHierarchies.length > 0) {
        parsed.userHierarchies = buildUserHierarchyStructure(parsed.userHierarchies);
      }

      setParsedData(parsed);

      // Initialize selected resource types (all enabled by default)
      const initialSelection = {};
      IMPORTABLE_RESOURCE_TYPES.forEach(type => {
        initialSelection[type] = parsed[type] && parsed[type].length > 0;
      });
      setSelectedResourceTypes(initialSelection);

      // Trigger validation
      if (selectedInstance) {
        await validateImport(parsed);
      } else {
        setImportStatus(IMPORT_STATUS.READY);
      }

    } catch (error) {
      console.error('Error parsing Excel file:', error);
      setErrorMessage(error.message || ERROR_MESSAGES.FILE_PARSE_ERROR);
      setImportStatus(IMPORT_STATUS.ERROR);
    }
  };

  /**
   * Validate import against existing resources
   */
  const validateImport = async (dataToValidate) => {
    if (!selectedInstance) {
      setErrorMessage(ERROR_MESSAGES.INSTANCE_NOT_SELECTED);
      return;
    }

    const data = dataToValidate || parsedData;
    if (!data) {
      setErrorMessage(ERROR_MESSAGES.FILE_NOT_UPLOADED);
      return;
    }

    setImportStatus(IMPORT_STATUS.VALIDATING);
    setErrorMessage(null);

    try {
      // Fetch existing resources from instance
      const existing = await listExistingResources(
        awsCredentials,
        awsCredentials.region,
        selectedInstance.Id
      );

      setExistingResources(existing);

      // Build name-to-ID maps for dependency resolution
      const maps = buildNameToIdMaps(existing);
      setNameToIdMaps(maps);

      // Run validation
      const validationService = new ImportValidationService(existing, data);
      const validation = validationService.validate();

      setValidationResults(validation.results);
      setValidationSummary(validationService.getSummary());
      setImportStatus(IMPORT_STATUS.READY);

    } catch (error) {
      console.error('Error validating import:', error);
      setErrorMessage(error.message || ERROR_MESSAGES.VALIDATION_ERROR);
      setImportStatus(IMPORT_STATUS.ERROR);
    }
  };

  /**
   * Handle resource type checkbox toggle
   */
  const handleResourceToggle = (resourceType) => {
    setSelectedResourceTypes(prev => ({
      ...prev,
      [resourceType]: !prev[resourceType]
    }));
  };

  /**
   * Handle import button click
   */
  const handleImport = async () => {
    // Validate selection
    const hasSelection = Object.values(selectedResourceTypes).some(selected => selected);
    if (!hasSelection) {
      setErrorMessage(ERROR_MESSAGES.NO_RESOURCES_SELECTED);
      return;
    }

    if (!selectedInstance) {
      setErrorMessage(ERROR_MESSAGES.INSTANCE_NOT_SELECTED);
      return;
    }

    if (!parsedData) {
      setErrorMessage(ERROR_MESSAGES.FILE_NOT_UPLOADED);
      return;
    }

    // Initialize import
    setImportStatus(IMPORT_STATUS.IMPORTING);
    setErrorMessage(null);
    setImportProgress({});
    setImportResults(null);

    // Create log service
    const logService = new ImportLogService();
    logService.start();
    setImportLog(logService);

    try {
      // Execute import
      const results = await importResources({
        parsedData,
        selectedResourceTypes,
        conflictResolution,
        perResourceConflicts,
        existingResources,
        nameToIdMaps,
        credentials: awsCredentials,
        region: awsCredentials.region,
        instanceId: selectedInstance.Id,
        onProgress: (progressUpdate) => {
          // Update progress state
          setImportProgress(prev => ({
            ...prev,
            [progressUpdate.resourceType]: {
              state: progressUpdate.state,
              current: progressUpdate.current,
              total: progressUpdate.total,
              message: progressUpdate.message,
              error: progressUpdate.error
            }
          }));
        },
        onLog: (resourceType, resourceName, action, details) => {
          // Log import action
          logService.logResourceAction(resourceType, resourceName, action, details);
        }
      });

      // Import complete
      logService.end();
      setImportResults(results);
      setImportStatus(IMPORT_STATUS.COMPLETE);

    } catch (error) {
      console.error('Import failed:', error);
      setErrorMessage(error.message || ERROR_MESSAGES.IMPORT_ERROR);
      setImportStatus(IMPORT_STATUS.ERROR);
      logService.end();
    }
  };

  /**
   * Handle preview button click
   */
  const handlePreview = (resourceType) => {
    setPreviewModalState({ opened: true, resourceType });
  };

  /**
   * Handle per-resource conflict resolution change
   */
  const handlePerResourceConflictChange = (resourceType, resourceName, resolution) => {
    setPerResourceConflicts(prev => ({
      ...prev,
      [resourceType]: {
        ...(prev[resourceType] || {}),
        [resourceName]: resolution
      }
    }));
  };

  /**
   * Get status badge for resource type
   */
  const getStatusBadge = (resourceType) => {
    if (!validationResults || !validationResults[resourceType]) {
      return null;
    }

    const result = validationResults[resourceType];
    const hasWarnings = result.dependencyWarnings && result.dependencyWarnings.length > 0;

    if (hasWarnings) {
      return (
        <Tooltip label={`${result.dependencyWarnings.length} warnings`}>
          <Badge color="yellow" leftSection={<IconAlertCircle size={14} />}>
            Warnings
          </Badge>
        </Tooltip>
      );
    }

    return null;
  };

  // Prepare instance options for Select
  const instanceOptions = instances.map(instance => ({
    value: instance.Id,
    label: instance.InstanceAlias || instance.Id
  }));

  return (
    <Layout onBackToPlatformSelector={onBackToPlatformSelector}>
      <Container size="xl" py="xl">
        <Stack gap="lg">
          {/* Header */}
          <div>
            <Title order={1}>Import Resources</Title>
            <Text c="dimmed" mt="xs">
              Import Amazon Connect resources from an Excel file to a target instance
            </Text>
          </div>

          {/* AWS Credential Change Section */}
          <AWSCredentialChangeSection
            awsCredentials={awsCredentials}
            setAwsCredentials={setAwsCredentials}
            onCredentialsUpdated={() => {
              // Reset import state when credentials change
              setInstances([]);
              setSelectedInstance(null);
              setUploadedFile(null);
              setParsedData(null);
              setValidationResults(null);
              setValidationSummary(null);
              setExistingResources(null);
              setNameToIdMaps(null);
              setImportStatus(IMPORT_STATUS.IDLE);
              setErrorMessage(null);
              // Reload instances with new credentials
              loadConnectInstances();
            }}
          />

          {/* No Credentials Alert */}
          {!awsCredentials && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="AWS Credentials Required"
              color="orange"
            >
              Please configure your AWS credentials above to continue with the import.
            </Alert>
          )}

          {/* Error Alert */}
          {errorMessage && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="red"
              withCloseButton
              onClose={() => setErrorMessage(null)}
            >
              {errorMessage}
            </Alert>
          )}

          {/* Step 1: Select Instance */}
          <Paper shadow="sm" p="md" withBorder>
            <Stack gap="md">
              <Group>
                <Badge color="aws" size="lg">Step 1</Badge>
                <Title order={3}>Select Target Instance</Title>
              </Group>

              <Select
                label="Connect Instance"
                placeholder="Select instance to import to"
                data={instanceOptions}
                value={selectedInstance?.Id}
                onChange={handleInstanceSelect}
                disabled={loadingInstances}
                leftSection={loadingInstances ? <Loader size="xs" /> : null}
                searchable
              />

              {selectedInstance && (
                <Alert icon={<IconInfoCircle size={16} />} color="blue">
                  <Text size="sm">
                    <strong>Selected Instance:</strong> {selectedInstance.InstanceAlias}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Region: {awsCredentials?.region}
                  </Text>
                </Alert>
              )}
            </Stack>
          </Paper>

          {/* Step 2: Upload File */}
          <Paper shadow="sm" p="md" withBorder>
            <Stack gap="md">
              <Group>
                <Badge color="aws" size="lg">Step 2</Badge>
                <Title order={3}>Upload Excel File</Title>
              </Group>

              <FileInput
                label="Excel File"
                placeholder="Click to select file or drag and drop"
                accept=".xlsx"
                value={uploadedFile}
                onChange={handleFileUpload}
                leftSection={<IconFileSpreadsheet size={16} />}
                disabled={importStatus === IMPORT_STATUS.VALIDATING}
              />

              {uploadedFile && (
                <Alert icon={<IconCheck size={16} />} color="green">
                  <Text size="sm">
                    <strong>File loaded:</strong> {uploadedFile.name}
                  </Text>
                  {parsedData && (
                    <Text size="sm" c="dimmed">
                      {Object.values(parsedData).reduce((sum, arr) => sum + (arr?.length || 0), 0)} total resources found
                    </Text>
                  )}
                </Alert>
              )}

              {importStatus === IMPORT_STATUS.VALIDATING && (
                <Group>
                  <Loader size="sm" />
                  <Text size="sm">Validating import data...</Text>
                </Group>
              )}
            </Stack>
          </Paper>

          {/* Step 3: Select Resources */}
          {importStatus === IMPORT_STATUS.READY && validationResults && (
            <Paper shadow="sm" p="md" withBorder>
              <Stack gap="md">
                <Group>
                  <Badge color="aws" size="lg">Step 3</Badge>
                  <Title order={3}>Select Resources to Import</Title>
                </Group>

                {/* Summary */}
                {validationSummary && (
                  <Alert icon={<IconInfoCircle size={16} />} color="blue">
                    <Group gap="xl">
                      <Text size="sm">
                        <strong>Total:</strong> {validationSummary.totalResources} resources
                      </Text>
                      <Text size="sm" c="green">
                        <strong>New:</strong> {validationSummary.totalNew}
                      </Text>
                      <Text size="sm" c="yellow">
                        <strong>Existing:</strong> {validationSummary.totalExisting}
                      </Text>
                      {validationSummary.totalWarnings > 0 && (
                        <Text size="sm" c="orange">
                          <strong>Warnings:</strong> {validationSummary.totalWarnings}
                        </Text>
                      )}
                    </Group>
                  </Alert>
                )}

                {/* Conflict Resolution Strategy */}
                <div>
                  <Text size="sm" fw={500} mb="xs">
                    Conflict Resolution Strategy:
                  </Text>
                  <Radio.Group value={conflictResolution} onChange={setConflictResolution}>
                    <Group>
                      <Radio
                        value={CONFLICT_RESOLUTION.SKIP}
                        label="Skip existing resources"
                      />
                      <Radio
                        value={CONFLICT_RESOLUTION.UPDATE}
                        label="Update existing resources"
                      />
                    </Group>
                  </Radio.Group>
                </div>

                {/* Resource Selection Table */}
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Import</Table.Th>
                      <Table.Th>Resource Type</Table.Th>
                      <Table.Th>Total</Table.Th>
                      <Table.Th>New</Table.Th>
                      <Table.Th>Existing</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {IMPORTABLE_RESOURCE_TYPES.map(resourceType => {
                      const result = validationResults[resourceType];
                      if (!result || result.total === 0) return null;

                      return (
                        <Table.Tr key={resourceType}>
                          <Table.Td>
                            <Checkbox
                              checked={selectedResourceTypes[resourceType] || false}
                              onChange={() => handleResourceToggle(resourceType)}
                            />
                          </Table.Td>
                          <Table.Td>
                            <Text fw={500}>{RESOURCE_TYPE_LABELS[resourceType]}</Text>
                          </Table.Td>
                          <Table.Td>{result.total}</Table.Td>
                          <Table.Td>
                            <Badge color="green" variant="light">{result.new}</Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge color="yellow" variant="light">{result.existing}</Badge>
                          </Table.Td>
                          <Table.Td>{getStatusBadge(resourceType)}</Table.Td>
                          <Table.Td>
                            <Button
                              size="xs"
                              variant="light"
                              leftSection={<IconEye size={14} />}
                              onClick={() => handlePreview(resourceType)}
                            >
                              View Details
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>

                {/* Import Button */}
                <Group justify="flex-end">
                  <Button
                    size="lg"
                    leftSection={<IconUpload size={20} />}
                    onClick={handleImport}
                    color="aws"
                    disabled={!Object.values(selectedResourceTypes).some(s => s) || importStatus === IMPORT_STATUS.IMPORTING}
                    loading={importStatus === IMPORT_STATUS.IMPORTING}
                  >
                    {importStatus === IMPORT_STATUS.IMPORTING ? 'Importing...' : 'Run Import'}
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {/* Import Progress Panel */}
          {importStatus === IMPORT_STATUS.IMPORTING && (
            <ImportProgressPanel
              importProgress={importProgress}
              importStatus={importStatus}
            />
          )}

          {/* Import Results */}
          {importStatus === IMPORT_STATUS.COMPLETE && importResults && (
            <Paper shadow="sm" p="md" withBorder>
              <Stack gap="md">
                <Group>
                  <Badge color="green" size="lg" leftSection={<IconCheck size={16} />}>
                    Import Complete
                  </Badge>
                  <Title order={3}>Import Results</Title>
                </Group>

                <Alert icon={<IconCheck size={16} />} color="green">
                  <Group gap="xl">
                    <Text size="sm">
                      <strong>Created:</strong> {importResults.success.length}
                    </Text>
                    <Text size="sm">
                      <strong>Skipped:</strong> {importResults.skipped.length}
                    </Text>
                    <Text size="sm">
                      <strong>Failed:</strong> {importResults.failed.length}
                    </Text>
                  </Group>
                </Alert>

                {importResults.failed.length > 0 && (
                  <Alert icon={<IconAlertCircle size={16} />} color="red" title="Failed Resources">
                    <Stack gap="xs">
                      {importResults.failed.slice(0, 5).map((item, index) => (
                        <Text key={index} size="sm">
                          • {item.resourceType}: {item.resourceName} - {item.error}
                        </Text>
                      ))}
                      {importResults.failed.length > 5 && (
                        <Text size="sm" c="dimmed">
                          ... and {importResults.failed.length - 5} more failures
                        </Text>
                      )}
                    </Stack>
                  </Alert>
                )}

              </Stack>
            </Paper>
          )}

          {/* Import Log Viewer */}
          {importStatus === IMPORT_STATUS.COMPLETE && importLog && (
            <ImportLogViewer
              importLog={importLog}
              importResults={importResults}
            />
          )}
        </Stack>
      </Container>

      {/* Preview Modal */}
      <ImportPreviewModal
        opened={previewModalState.opened}
        onClose={() => setPreviewModalState({ opened: false, resourceType: null })}
        resourceType={previewModalState.resourceType}
        validationResults={validationResults}
        parsedData={parsedData}
        globalConflictResolution={conflictResolution}
        perResourceConflicts={perResourceConflicts}
        onConflictResolutionChange={handlePerResourceConflictChange}
      />

    </Layout>
  );
};

export default AWSImportPage;
